/**
 * A side drawer previewing one analysis from the Analyses list: its status,
 * the last run's numbers and whether its Excel can be downloaded, with the
 * way on to the full analysis page. Reads only what the list already loaded.
 *
 * Laid out to be read at a glance: the four counts as figure tiles, the
 * details as one compact list, reports as a plain list (a status shows only
 * when a report is not completed), and two actions in the footer.
 */

import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
	Box,
	Button,
	Link as MuiLink,
	Tooltip,
	Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Note, SideDrawer } from '../../shared/ui';
import { count } from '../../utils/format';
import { useModule } from '../modules/useModule';
import { descriptionLines } from './AnalysisInfo';
import {
	excelDownloadReason,
	previewFacts,
	type AnalysisRow,
	type PreviewFact,
} from './analysisRows';
import { ExportAnalysisDialog } from './ExportAnalysisDialog';
import { listStatus, ListStatusBadge } from './ListStatusBadge';

const sectionLabel = {
	fontSize: 'var(--fs-small)',
	fontWeight: 600,
	color: 'var(--ink-2)',
	mb: 'var(--s2)',
} as const;

/** One count as a small tile: the label over the figure. */
function FigureTile({ fact }: { fact: PreviewFact }) {
	const numeric = /^[\d,]+$/.test(fact.value);
	return (
		<Box
			sx={{
				p: 'var(--s2) var(--s3)',
				border: '1px solid var(--line)',
				borderRadius: 'var(--r-lg)',
				bgcolor: 'var(--surface)',
				minWidth: 0,
			}}
		>
			<Typography
				sx={{ fontSize: 'var(--fs-small)', color: 'var(--ink-3)' }}
			>
				{fact.label}
			</Typography>
			<Typography
				title={fact.value}
				sx={{
					fontSize: numeric ? 'var(--fs-figure)' : 'var(--fs-small)',
					fontWeight: numeric ? 600 : 400,
					color: numeric ? 'var(--ink)' : 'var(--ink-3)',
					fontVariantNumeric: 'tabular-nums',
					mt: '2px',
				}}
			>
				{fact.value}
			</Typography>
		</Box>
	);
}

/** Label / value pairs in two aligned columns, without dividers. */
function DetailList({ facts }: { facts: PreviewFact[] }) {
	return (
		<Box
			component="dl"
			sx={{
				display: 'grid',
				gridTemplateColumns: '120px minmax(0, 1fr)',
				columnGap: 'var(--s3)',
				rowGap: '6px',
				m: 0,
				fontSize: 'var(--fs-body)',
			}}
		>
			{facts.map((fact) => (
				<Box key={fact.label} sx={{ display: 'contents' }}>
					<Box component="dt" sx={{ color: 'var(--ink-3)' }}>
						{fact.label}
					</Box>
					<Box component="dd" sx={{ m: 0, color: 'var(--ink)' }}>
						{fact.value}
					</Box>
				</Box>
			))}
		</Box>
	);
}

export function AnalysisPreview({
	entry: current,
	auditId,
	analysisPath,
	onClose,
	downloadExcel,
	downloading,
}: {
	/** Null = closed. */
	entry: AnalysisRow | null;
	auditId: string;
	analysisPath: (entry: AnalysisRow) => string;
	onClose: () => void;
	downloadExcel: (number: number) => void;
	/** The analysis number being downloaded, if any. */
	downloading: number | null;
}) {
	const { module } = useModule();
	const [exporting, setExporting] = useState(false);
	/* Keep showing the last analysis while the drawer slides out. */
	const last = useRef(current);
	if (current) last.current = current;
	/* Never carry an open Export dialog over to the next preview. */
	if (!current && exporting) setExporting(false);
	const entry = current ?? last.current;
	const rollup = entry?.rollup ?? null;
	const facts = rollup ? previewFacts(rollup, module) : null;
	const completed = rollup?.status === 'COMPLETED';
	const reason =
		entry && rollup
			? excelDownloadReason(
					{ ...rollup, listStatus: listStatus(rollup) },
					downloading !== null && downloading !== entry.number,
				)
			: null;

	/* Details: what it reads and when it last ran, in one list. */
	const details: PreviewFact[] = facts
		? (() => {
				const [started, finished, duration] = facts.run;
				const took =
					duration && duration.value !== '—'
						? ` · took ${duration.value}`
						: '';
				return [
					...facts.availability,
					{ label: 'Started', value: started?.value ?? '—' },
					{
						label: 'Completed',
						value: `${finished?.value ?? '—'}${took}`,
					},
				];
			})()
		: [];
	/* The tiles, most read first. */
	const tiles = facts
		? [
				'Result rows',
				'Exception findings',
				'Supporting rows',
				'Rows analysed',
			]
				.map((label) => facts.rows.find((fact) => fact.label === label))
				.filter((fact): fact is PreviewFact => fact !== undefined)
		: [];

	return (
		<SideDrawer
			open={current !== null}
			onClose={onClose}
			width={520}
			title={
				entry ? (
					/* The status sits right after the name; it wraps under it only
             when the name is too long for one line. */
					<Box
						component="span"
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							flexWrap: 'wrap',
							columnGap: 'var(--s2)',
							rowGap: '4px',
						}}
					>
						<span>{`${entry.number}. ${entry.title}`}</span>
						{rollup && <ListStatusBadge row={rollup} />}
					</Box>
				) : (
					''
				)
			}
			subtitle={
				entry ? (
					<MuiLink
						component={Link}
						to={analysisPath(entry)}
						underline="hover"
						sx={{ fontSize: 'var(--fs-small)', fontWeight: 600 }}
					>
						Open full analysis →
					</MuiLink>
				) : undefined
			}
			footer={
				entry && rollup ? (
					<Box
						display="flex"
						gap="var(--s2)"
						justifyContent="flex-end"
					>
						<Tooltip
							title={
								completed
									? ''
									: 'Available once the analysis has completed.'
							}
							disableHoverListener={completed}
						>
							<span>
								<Button
									variant="outlined"
									disabled={!completed}
									onClick={() => setExporting(true)}
								>
									Export…
								</Button>
							</span>
						</Tooltip>
						<Tooltip
							title={reason ?? ''}
							disableHoverListener={!reason}
						>
							<span>
								<Button
									variant="contained"
									startIcon={<FileDownloadOutlinedIcon />}
									loading={downloading === entry.number}
									disabled={
										Boolean(reason) || downloading !== null
									}
									onClick={() => downloadExcel(entry.number)}
								>
									Download Excel
								</Button>
							</span>
						</Tooltip>
					</Box>
				) : undefined
			}
		>
			{entry && (
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: 'var(--s4)',
					}}
				>
					{entry.description && (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: '2px',
								fontSize: 'var(--fs-body)',
								color: 'var(--ink-2)',
							}}
						>
							{descriptionLines(entry.description).map((line) => (
								<Box key={line}>{line}</Box>
							))}
						</Box>
					)}

					{(entry.onHold !== null || !rollup) && (
						<Note tone="info">
							{entry.onHold ?? 'This analysis is on hold.'}
						</Note>
					)}

					{rollup && facts && (
						<>
							{rollup.error && (
								<Note tone="danger" title="Last run failed">
									{rollup.error}
								</Note>
							)}
							{rollup.outdated && (
								<Note tone="warn" title="Out of date">
									The data scope changed after this ran.
									Re-run to update it.
								</Note>
							)}
							{rollup.availability === 'BLOCKED' && (
								<Note tone="warn" title="Blocked">
									{[
										rollup.blockReason,
										rollup.missingColumns.length > 0
											? `Missing: ${rollup.missingColumns.map((column) => column.label).join(', ')}.`
											: null,
									]
										.filter(Boolean)
										.join(' ')}
								</Note>
							)}
							{/* Only a completed analysis without a workbook needs saying. */}
							{completed && !rollup.hasExcel && (
								<Note
									tone="warn"
									title="No Excel for this result"
								>
									{rollup.excelUnavailableReason ??
										'There is no Excel file for this result.'}
								</Note>
							)}

							<Box
								sx={{
									display: 'grid',
									gridTemplateColumns:
										'repeat(2, minmax(0, 1fr))',
									gap: 'var(--s2)',
								}}
							>
								{tiles.map((fact) => (
									<FigureTile key={fact.label} fact={fact} />
								))}
							</Box>

							<Box>
								<Typography sx={sectionLabel}>
									Details
								</Typography>
								<DetailList facts={details} />
							</Box>

							{entry.reports.length > 1 && (
								<Box>
									<Typography sx={sectionLabel}>
										Reports
									</Typography>
									<Box
										role="list"
										sx={{
											border: '1px solid var(--line)',
											borderRadius: 'var(--r-lg)',
											overflow: 'hidden',
										}}
									>
										{entry.reports.map(
											(report, position) => (
												<Box
													key={report.row.slug}
													role="listitem"
													sx={{
														display: 'flex',
														alignItems: 'center',
														gap: 'var(--s2)',
														minHeight: 36,
														px: 'var(--s3)',
														borderTop:
															position === 0
																? 'none'
																: '1px solid var(--line)',
														fontSize:
															'var(--fs-body)',
													}}
												>
													<Box
														component="span"
														sx={{
															color: 'var(--ink-3)',
															flex: '0 0 auto',
														}}
													>
														{report.index}.
													</Box>
													<Typography
														noWrap
														title={report.label}
														sx={{
															flex: 1,
															minWidth: 0,
															fontSize: 'inherit',
															color: 'var(--ink)',
														}}
													>
														{report.label}
													</Typography>
													{/* Completed is the normal state: only anything else is shown. */}
													{report.row.status !==
														'COMPLETED' && (
														<ListStatusBadge
															row={report.row}
														/>
													)}
													<Box
														component="span"
														sx={{
															flex: '0 0 auto',
															color: 'var(--ink-3)',
															fontVariantNumeric:
																'tabular-nums',
														}}
													>
														{count(
															report.row
																.resultRows,
														)}{' '}
														rows
													</Box>
												</Box>
											),
										)}
									</Box>
								</Box>
							)}
						</>
					)}
				</Box>
			)}
			<ExportAnalysisDialog
				open={exporting && current !== null}
				onClose={() => setExporting(false)}
				auditId={auditId}
				entry={current}
			/>
		</SideDrawer>
	);
}
